import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { StudentListHeader } from '~/components/ui/ContentNav';
import { PageTitle } from '~/components/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { Table } from '~/components/ui/Table';
import { useStudentFocus } from '~/modules/studentFocus';

type QuizResponseSummary = components['schemas']['QuizResponseSummary'];

type QuizSubmissionsSearch = {
  user_id?: number;
};

export const Route = createFileRoute('/_admin/quiz-submissions/')({
  component: QuizSubmissionsList,
  validateSearch: (search: Record<string, unknown>): QuizSubmissionsSearch => {
    const raw = search.user_id;
    const id =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw !== ''
          ? Number(raw)
          : undefined;
    return {
      user_id: id !== undefined && Number.isFinite(id) ? id : undefined,
    };
  },
});

const columnHelper = createColumnHelper<QuizResponseSummary>();

function statusBadge(row: QuizResponseSummary): {
  label: string;
  variant: BadgeVariant;
} {
  if (!row.complete) return { label: 'In progress', variant: 'neutral' };
  if (row.passed) return { label: 'Passed', variant: 'success' };
  return { label: 'Failed', variant: 'error' };
}

const columns = [
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('user_display_name', {
    header: 'Student',
    cell: (info) => (
      <div>
        <div>{info.getValue()}</div>
        <small style={{ color: 'var(--grey-600)' }}>
          {info.row.original.user_email}
        </small>
      </div>
    ),
  }),
  columnHelper.accessor('course_name', { header: 'Course' }),
  columnHelper.accessor('lesson_name', { header: 'Lesson' }),
  columnHelper.accessor('score', {
    header: 'Score',
    cell: (info) => `${info.getValue()}%`,
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) => {
      const { label, variant } = statusBadge(info.row.original);
      return <Badge $variant={variant}>{label}</Badge>;
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Submitted',
    cell: (info) => new Date(info.getValue()).toLocaleString(),
  }),
];

function QuizSubmissionsList() {
  const search = Route.useSearch();
  const [page, setPage] = useState(0);
  const { focusedStudent } = useStudentFocus();

  const effectiveUserId = search.user_id ?? focusedStudent?.id;

  const { data, isLoading, error } = api.listQuizResponses.useQuery({
    query: {
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
      ...(effectiveUserId !== undefined ? { user_id: effectiveUserId } : {}),
    },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      {search.user_id !== undefined ? (
        <StudentListHeader
          studentId={search.user_id}
          title="Quiz submissions"
        />
      ) : (
        <PageTitle>Quiz Submissions</PageTitle>
      )}

      {isLoading ? (
        <LoadingMessage>Loading Quiz Submissions...</LoadingMessage>
      ) : error ? (
        <ErrorMessage>Error loading Quiz Submissions</ErrorMessage>
      ) : (
        <>
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
          />

          <Table
            table={table}
            to="/quiz-submissions/$submissionId"
            getParams={(row) => ({ submissionId: String(row.id) })}
          />
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            shown={data?.data.length ?? 0}
            total={data?.count}
            onPage={setPage}
            hideWhenAlone
          />
        </>
      )}
    </div>
  );
}

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
